import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Command, CommandContribution, CommandRegistry } from '@patsnap/tahiti-core/common/command';
import { I18nService } from '@telecom/shared/services/i18n';
import { i18nDeferred } from '@/infrastructure/locales/deferred';
import { SpecificationParagraphAiEditType } from '@/domains/specification/enums';
import EmbodimentImprovement from '@/presentation/pages/specification/components/embodiment-improvement/EmbodimentImprovement.vue';
import type { Component, DefineComponent } from 'vue';
import IconMagicStar from '@/presentation/assets/images/icons/icon-magic-star.svg';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import { GenEmbodimentApi } from '@/domains/specification/apis/gen-embodiment-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { SpecificationOperateType } from '@/domains/specification/enums';
import type { PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import type { SpecificationStreamContent } from '@/domains/specification/types/specification';
import { SpecificationEmbodimentImprovementModel } from '@/presentation/pages/specification/components/embodiment-improvement/models/specification-embodiment-improvement-model';

interface CustomCommandRegistration extends Command {
    component?: Component | DefineComponent;
}

@injectable()
export class ImproveEmbodimentCommandContribution implements CommandContribution {
    constructor(
        @inject(I18nService)
        private _i18nService: I18nService,
        @inject(GenEmbodimentApi)
        private readonly _genEmbodimentApi: GenEmbodimentApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(SpecificationEmbodimentImprovementModel)
        private readonly _improvementModel: SpecificationEmbodimentImprovementModel,
    ) {
    }

    private _stream: PsHttpSSE<SpecificationStreamContent<string>> | null = null;

    async generateContent(callback: (status: StreamStatus, content?: string) => void) {
        const outline = this._improvementModel.modelData.modifiedOutline;
        if (!outline || !outline.embodiment_number) return;
        // TODO: 优化实施例埋点
        this._stream = this._genEmbodimentApi.genEmbodimentFromOutline({
            task_id: this._currentTaskService.taskId,
            operate_type: SpecificationOperateType.Regenerate,
            embodiment_number: outline.embodiment_number!,
            ...outline,
        }).onMessage(data => {
            const { status, content } = data.data ?? {};
            callback(status!, content);
        })
            .onFinish(() => {
                callback(StreamStatus.FINISH);
            })
            .onError(() => {
                callback(StreamStatus.FAILED);
            })
            .onClose(() => {
                callback(StreamStatus.FINISH);
            });

        this._stream && this._stream.start();
    }

    stopGenerate() {
        this._stream?.close();
    }

    async registerCommands(commands: CommandRegistry): Promise<void> {
        await i18nDeferred.promise;

        commands.registerCommand({
            id: SpecificationParagraphAiEditType.ImproveEmbodiment,
            label: this._i18nService.t('aiSpecification.embodiment.improveEmbodiment'),
            icon: IconMagicStar,
            component: EmbodimentImprovement,
        } as CustomCommandRegistration, {
            execute: () => {
                return {
                    getContentOpt: (callback: (status: StreamStatus, content?: string) => void) => this.generateContent(callback),
                    stopGenerate: () => this.stopGenerate(),
                };
            },
        });
    }
}
